import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Text,
  FlexBox,
  FlexBoxJustifyContent,
  ObjectStatus
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { formatNumberWithComma, rightAlignedNumberStyle, getStatusStyle, getStatusComponentProps } from '../utils/validator';
import useAutoTranslate from '../hooks/useAutoTranslate'; // Importación agregada

const GrnList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'grnNumber',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    grnNumber: '',
    asnNumber: ''
  });
  const [tempSearchParams, setTempSearchParams] = useState({
    grnNumber: '',
    asnNumber: ''
  });
  const [shouldSearch, setShouldSearch] = useState(false);
  const [asnDetails, setAsnDetails] = useState({});

  // Hook de traducción
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    if (shouldSearch) {
      const debounceTimeout = setTimeout(() => {
        setSearchParams(tempSearchParams);
        setCurrentPage(1);
        setShouldSearch(false);
      }, 500);
      
      return () => clearTimeout(debounceTimeout);
    }
  }, [shouldSearch, tempSearchParams]);

  const handleSearchInputChange = (field, value) => {
    setTempSearchParams(prev => ({
      ...prev,
      [field]: typeof value === 'string' ? value.trim() : value
    }));
    setShouldSearch(true);
  };

  const handleResetFilters = () => {
    setTempSearchParams({
      grnNumber: '',
      asnNumber: ''
    });
    
    setSearchParams({
      grnNumber: '',
      asnNumber: ''
    });
    
    setCurrentPage(1);
    fetchGrns(1);
  };

  const fetchGrns = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page,
        size: pageSize,
        ...(searchParams.grnNumber && { grnNumber: searchParams.grnNumber.trim() }),
        ...(searchParams.asnNumber && { asnNumber: searchParams.asnNumber.trim() })
      });

      const url = `grn/search?${queryParams.toString()}`;
      const response = await axiosInstance.get(url);
      
      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError(autoT('Error al cargar los Entregas: ') + (err.message || autoT('Error desconocido')));
    } finally {
      setLoading(false);
    }
  }, [pageSize, searchParams]);

  useEffect(() => {
    fetchGrns(currentPage);
  }, [currentPage, fetchGrns]);

  useEffect(() => {
    let filtered = [...data];
    
    if (searchParams.grnNumber || searchParams.asnNumber) {
      filtered = filtered.filter(grn => 
        grn.grnNumber?.toLowerCase().includes(searchParams.grnNumber.toLowerCase()) ||
        grn.asnNumber?.toLowerCase().includes(searchParams.asnNumber.toLowerCase())
      );
    }
    
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, searchParams, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (event) => {
    setSearchParams(prev => ({
      ...prev,
      grnNumber: event.target.value,
      asnNumber: event.target.value
    }));
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const fetchAsnDetails = async (asnDocumentIds) => {
    try {
      const asnDetailPromises = asnDocumentIds.map(asnId =>
        axiosInstance.get(`/asn-documents/${asnId}`)
      );
      const asnDetailResults = await Promise.allSettled(asnDetailPromises);
      
      const fetchedAsnDetails = {};
      asnDetailResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.data) {
          const asnData = result.value.data.data || result.value.data;
          const asnId = asnDocumentIds[index];
          fetchedAsnDetails[asnId] = asnData.asnNumber;
        }
      });
      setAsnDetails(fetchedAsnDetails);
    } catch (err) {
      console.error(autoT("Error fetching ASN details:"), err);
    }
  };

  useEffect(() => {
    const asnIds = data.map(grn => grn.asnDocumentId).filter(Boolean);
    if (asnIds.length > 0) {
      fetchAsnDetails(asnIds);
    }
  }, [data]);

  const getStandardStatus = (status) => {
    const statusMap = {
      'Completed': 'COMPLETED',
      'PENDING': 'PENDING',
      'CANCELLED': 'CANCELLED',
      'IN_PROGRESS': 'IN_PROGRESS',
      'Pending': 'PENDING',
      'Canceled': 'CANCELLED',
      'In Progress': 'IN_PROGRESS',
      'Complete': 'COMPLETED'
    };
    return statusMap[status] || status;
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'COMPLETED': autoT('Completado'),
      'PENDING': autoT('Pendiente'),
      'CANCELLED': autoT('Cancelado'),
      'IN_PROGRESS': autoT('En Progreso')
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      Header: autoT('N° Notificación'),
      accessor: 'asnDocumentId',
      headerProps: {
        onClick: () => handleSort('asnDocumentId')
      },
      className: sortConfig.sortBy === 'asnDocumentId' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ row }) => {
        const asnId = row.original.asnDocumentId;
        if (!asnId) return null;

        return (
          <Button
            design="Transparent"
            onClick={() => navigate(`/asn/${asnId}`)}
          >
            {asnDetails[asnId] || autoT("Cargando...")}
          </Button>
        );
      }
    },
    {
      Header: autoT('N° Entrega'),
      accessor: 'grnNumber',
      headerProps: {
        onClick: () => handleSort('grnNumber')
      },
      className: sortConfig.sortBy === 'grnNumber' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Cantidad'),
      accessor: 'receivedQuantity',
      headerProps: {
        onClick: () => handleSort('receivedQuantity')
      },
      className: sortConfig.sortBy === 'receivedQuantity' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
    },
    {
      Header: autoT('Estado'),
      accessor: 'grnStatus',
      headerProps: {
        onClick: () => handleSort('grnStatus')
      },
      className: sortConfig.sortBy === 'grnStatus' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ row }) => {
        const rawStatus = row.original.grnStatus;
        const standardStatus = getStandardStatus(rawStatus);
        const displayLabel = getStatusLabel(standardStatus);
        
        return (
          <ObjectStatus
            {...getStatusComponentProps(standardStatus, null, { width: '120px' })}
          >
            {displayLabel}
          </ObjectStatus>
        );
      }
    }
  ];

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: '15px',
      marginBottom: '20px',
      position: 'relative',
      paddingBottom: '80px'
    }}>
      <Title level="H2">{autoT('Lista de entregas')}</Title>
      
      {error && (
        <div style={{ color: 'red', margin: '1rem 0' }}>
          {error}
        </div>
      )}
      
      {/* Search Filters Section */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <Title level="H4" style={{ marginBottom: '1rem' }}>{autoT('Búsqueda avanzada')}</Title>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('N° Notificación')}
            </label>
            <Input 
              value={tempSearchParams.asnNumber}
              onChange={(e) => handleSearchInputChange('asnNumber', e.target.value)}
              placeholder={autoT("Ingrese número de notificación")}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('N° Entrega')}
            </label>
            <Input 
              value={tempSearchParams.grnNumber}
              onChange={(e) => handleSearchInputChange('grnNumber', e.target.value)}
              placeholder={autoT("Ingrese número de entrega")}
              style={{ width: '100%' }}
            />
          </div>
          
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            design="Transparent" 
            onClick={handleResetFilters}
          >
            {autoT('Limpiar filtros')}
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <BusyIndicator active size="Large" />
        </div>
      ) : (
        <>
          <AnalyticalTable
            data={filteredData}
            columns={columns}
            selectionMode="None"
            style={{ marginTop: '1rem' }}
            visibleRows={10}
            minRows={10}
            alternateRowColor
          />
          
          <div style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
            borderTop: '1px solid #e5e5e5',
            zIndex: 100
          }}>
            <div style={{ width: '100%', maxWidth: '98%', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button onClick={handlePrevPage} disabled={currentPage === 1}>
                ← {autoT('Anterior')}
              </Button>
              <span style={{ flex: 1, textAlign: 'center' }}>
                {autoT('Página')} {currentPage} {autoT('de')} {totalPages}
              </span>
              <Button onClick={handleNextPage} disabled={currentPage === totalPages}>
                {autoT('Siguiente')} →
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GrnList;